<template>
  <div class="chats ta-chat-log">
    <template v-if="busy !== true">
      <div v-if="!loading && formattedChatData.length === 0" class="text-center text-muted mt-2">
        {{ $t('chat.log.empty') }}
      </div>
      <div
        v-for="msgGrp in formattedChatData"
        :key="msgGrp.key"
        class="chat"
        :class="{'chat-left': msgGrp.senderId !== myId, 'chat-other-user': msgGrp.senderId !== myId && !msgGrp.isClientSide}"
      >
        <div class="chat-avatar">
          <b-avatar
              v-if="!noAvatar && msgGrp.senderId === myId && profileUserAvatar"
              size="36"
              class="avatar-border-2 box-shadow-1"
              variant="transparent"
              :src="require(`@/assets/images/avatars/${profileUserAvatar}-small.png`)"
          />
        </div>
        <div class="chat-body">
          <div v-if="msgGrp.senderName" class="ta-chat-sender">{{ msgGrp.senderName }}</div>
          <div
            v-for="msgData in msgGrp.messages"
            :key="msgData.key"
            class="chat-content"
            :class="{'ta-chat-pending': msgData.pending && !msgData.failed, 'ta-chat-failed': msgData.failed}"
          >
            <p class="ta-chat-text">{{ msgData.msg }}</p>
            <div class="ta-chat-meta">
              <span>{{ msgData.time }}</span>
              <template v-if="msgGrp.senderId === myId || !msgGrp.isClientSide">
                <span v-if="msgData.failed" class="ta-chat-retry" @click="retry(msgData)">
                  <feather-icon icon="AlertCircleIcon" size="12"/> {{ $t('chat.log.failedRetry') }}
                </span>
                <feather-icon v-else-if="msgData.pending" icon="ClockIcon" size="12"/>
                <span v-else-if="msgData.clientHasSeen" v-b-tooltip.hover :title="$t('chat.log.seen')">
                  <i class="fa-solid fa-check-double"></i>
                </span>
                <span v-else v-b-tooltip.hover :title="$t('chat.log.sent')">
                  <i class="fa-solid fa-check"></i>
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-if="(loading && formattedChatData.length === 0) || busy" class="text-center">
      <b-spinner label="Loading..." />
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  data() {
    return {
      loading: false,
      intervalGetChatDataId: null,
    }
  },
  props: {
    session: {
      type: Object,
      required: true,
    },
    profileUserAvatar: {
      type: String,
      required: true,
    },
    noAvatar: {
      type: Boolean,
      default: false,
    },
    busy:{
      type: Boolean,
      default: false,
    }
  },
  watch:{
    // Watch only the id: the session object is replaced on every refresh (sessions list,
    // read receipts...) and reloading on each of those caused the spinner/jump loop.
    sessionId:{
      handler(){
        this.refreshChat()
      },
      immediate:true,
    },
  },
  created() {
    this.intervalGetChatDataId = setInterval(() => this.fetchChatMessages(), 10*1000); // Fallback poll every 10 seconds
    this.$root.$on("system::notification::CHAT",this.onChatNotification)
  },
  beforeDestroy() {
    clearInterval(this.intervalGetChatDataId);
    this.$root.$off("system::notification::CHAT",this.onChatNotification)
  },
  computed:{
    ...mapGetters("user",["myId"]),
    ...mapGetters("chat",["chatBySessionId"]),
    sessionId() {
      return this.session?.id || null
    },
    chatData() {
      return this.sessionId != null ? this.chatBySessionId(this.sessionId) : null
    },
    participantNames() {
      const names = {}
      ;(this.session?.chatParticipants || []).forEach(p => { names[p.userId] = p.userName })
      return names
    },
    formattedChatData() {
      const chatLog = this.chatData?.messages || []
      const groups = []
      let group = null
      chatLog.forEach(msg => {
        if (group == null || group.senderId !== msg.senderId) {
          const isOtherAgent = msg.senderId !== this.myId && !msg.isClientSide
          group = {
            key: 'g-' + (msg.localKey || msg.messageId),
            senderId: msg.senderId,
            isClientSide: msg.isClientSide,
            senderName: isOtherAgent ? (this.participantNames[msg.senderId] || null) : null,
            messages: [],
          }
          groups.push(group)
        }
        group.messages.push({
          key: msg.localKey || msg.messageId,
          messageId: msg.messageId,
          msg: msg.content,
          time: this.formatTime(msg.timestamp),
          pending: msg.pending === true,
          failed: msg.failed === true,
          clientHasSeen: msg.clientHasSeen === true,
        })
      })
      return groups
    },
  },
  methods:{
    ...mapActions("chat", ["getChatMessages", "setMessagesRead", "receiveMessage", "sendChatMessage"]),
    ...mapMutations("chat", ["setTyping", "markClientSeen"]),
    onChatNotification(payload){
      const params = payload?.params || {}
      if (params.sessionId == null || params.sessionId !== this.sessionId) return
      switch (params.type) {
        case 'chatMessage':
          if (params.message) {
            this.receiveMessage(params.message)
          } else {
            this.fetchChatMessages()
          }
          this.markRead()
          break
        case 'clientTyping':
          this.setTyping(params.sessionId)
          break
        case 'clientRead':
          this.markClientSeen(params.sessionId)
          break
      }
    },
    refreshChat(){
      if(this.sessionId == null){
        return;
      }
      this.loading = true
      this.fetchChatMessages(true);
      this.markRead()
    },
    fetchChatMessages(initial){
      const sessionId = this.sessionId
      if (sessionId == null) return
      this.getChatMessages({ sessionId }).then(() => {
        if (sessionId !== this.sessionId) return
        if (initial === true) {
          this.$nextTick(() => this.$emit('loaded', sessionId))
        }
      }).catch(error => {
        console.error('Error fetching chat messages:', error)
      }).finally(() => {
        if (sessionId === this.sessionId) this.loading = false
      })
    },
    markRead(){
      if (this.sessionId == null) return
      this.setMessagesRead({ sessionId: this.sessionId }).catch(() => {})
    },
    retry(msgData){
      this.sendChatMessage({
        sessionId: this.sessionId,
        tempId: msgData.messageId,
        messageRequest: { content: msgData.msg },
      }).catch(error => console.error('Error resending message:', error))
    },
    formatTime(timestamp){
      if (!timestamp) return ''
      const time = this.$moment.utc(timestamp, 'YYYY-MM-DD HH:mm').local()
      if (!time.isValid()) return ''
      if (this.$moment().isSame(time, 'day')) {
        return time.format('HH:mm')
      }
      return time.format('DD/MM HH:mm')
    },
  }
}
</script>

<style lang="scss">
.chat-app-window .ta-chat-log {
  .chat-body .chat-content {
    max-width: 75%;
    .ta-chat-text {
      white-space: pre-wrap;
      word-break: break-word;
    }
    .ta-chat-meta {
      font-size: 0.7rem;
      opacity: 0.75;
      text-align: right;
      margin-top: 2px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 6px;
    }
    &.ta-chat-pending {
      opacity: 0.65;
    }
    &.ta-chat-failed {
      opacity: 1;
      outline: 1px solid var(--danger, #ea5455);
      .ta-chat-retry {
        cursor: pointer;
        font-weight: 600;
        text-decoration: underline;
      }
    }
  }
  .ta-chat-sender {
    font-size: 0.75rem;
    opacity: 0.7;
    margin: 0 1rem 2px 1rem;
  }
  .chat:not(.chat-left) .ta-chat-sender {
    text-align: right;
  }
}
</style>
